// QualityAnnotation — human-scored evaluation of an AI-produced item.
// Sprint 19BG+1 Phase 4 EVAL-ANNOTATION-UI · `DEBT-19BG+1-EVAL-ANNOTATION-UI`.
//
// Why this exists (per docs/current/sprint.md §3.4):
//   The 4-dimension rubric (fidelity / dispersion / fictional / cog) is half
//   automated (jaccard / parity / lint flags) and half not. Two dimensions —
//   `fictional` and `cog` — need a human to score before we can calibrate
//   the automated halves or trust the bench. Phase 4 is the surface where
//   the principal (and later, outsourced reviewers) deposit those scores.
//
// Schema philosophy:
//   - `scores` is a `z.record(string, 0-3)` not a fixed shape — adding a 5th
//     dimension never bumps schemaVersion. Rubric definition lives in
//     `rubric-v1.ts` (data-driven UI).
//   - `reviewerId` is mandatory on day 1 even though v1 is single-user — so
//     when a server-side multi-reviewer mode arrives, the schema doesn't
//     change and old single-user annotations stay readable.
//   - `passthrough()` policy (per schema-version.ts §2.4) — server may stamp
//     extra fields on the way in; we preserve them verbatim.
//   - `rubricVersion` field is the lenient-comparison key (see
//     `meetsMinSchemaVersion`) — vintage rubric data shows a badge, doesn't
//     crash.
//
// What `itemKind` covers:
//   The 7 kinds map to the inventory the AI ingest pipeline produces. New
//   item types added later require enum extension (a MINOR rubric bump).
//
// Score scale (per sprint §3.4):
//   0 = fail · 1 = poor · 2 = acceptable · 3 = excellent.
//   Integer only — half-points are noise at sample sizes we work with.
import { z } from 'zod';
/** The kinds of items a reviewer can score. Matches IngestPackage inventory. */
export const QualityAnnotationItemKindSchema = z.enum([
    'mcq',
    'tf',
    'flashcard',
    'essay',
    'glossary',
    'case',
    'crossPair',
]);
/** A single 0-3 integer score on one rubric dimension. */
export const QualityAnnotationScoreSchema = z.number().int().min(0).max(3);
/** Where the candidate item came from (which sample batch / file).
 *  Lets us audit "this annotation was produced when reviewing batch X" later. */
export const QualityAnnotationSourceSchema = z.object({
    batch: z.string(), // free-form id e.g. 'cs3052-day2-cdf563d1'
    candidateFile: z.string().optional(), // path of the candidate JSON
}).passthrough();
/** Top-level annotation. One row per (item, reviewer, time). Keyed by `id`. */
export const QualityAnnotationSchema = z.object({
    id: z.string(), // uuid v4
    rubricVersion: z.string(), // e.g. '1.0.0'
    itemKind: QualityAnnotationItemKindSchema,
    itemId: z.string(),
    courseId: z.string().optional(),
    caseId: z.string().optional(),
    runId: z.string().optional(), // D1 ingest_runs id (when known)
    /** dim → 0..3 integer. Empty record means "no scores yet (draft)". */
    scores: z.record(z.string(), QualityAnnotationScoreSchema),
    /** Reviewer-flagged tags. Examples: 'fictional', 'misaligned',
     *  'option-bias'. Free-form strings — not enum'd, so reviewers can add new
     *  tags without a schema bump. */
    flags: z.array(z.string()).default([]),
    comment: z.string().max(2000).optional(),
    reviewerId: z.string(), // 'local' default; server fills real id
    createdAt: z.number(), // epoch ms
    updatedAt: z.number(), // epoch ms
    source: QualityAnnotationSourceSchema,
}).passthrough();
//# sourceMappingURL=quality-annotation.js.map